<script setup lang="ts">
import type { PostCustomPropertyValue } from '@@/types'
import type { PostPropertyDisplayAttributes } from '@@/vuecomponents/SurfacePostProperties.vue'
import { processPostBody } from '@@/vuecomposables/process_post_or_comment_body'
import { onMounted, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{
    darkMode: boolean
    postProperty: PostPropertyDisplayAttributes
    propertyValueClass?: string
    as?: string
  }>(),
  {
    propertyValueClass: 'text-body-small',
    as: 'dd',
  },
)

function assertIsString(value: unknown): asserts value is string {
  if (typeof value !== 'string') {
    throw new Error('Expected string but got: ' + typeof value)
  }
}

const textFieldValue = ref(props.postProperty.formattedValue)
const textFieldValueRef = ref<HTMLElement | null>(null)
const processTextFieldValue = async (value: PostCustomPropertyValue): Promise<void> => {
  if (!textFieldValueRef.value) return
  assertIsString(value)
  const string = await processPostBody(value, textFieldValueRef.value)
  textFieldValue.value = string
}

onMounted(async () => {
  await processTextFieldValue(props.postProperty.formattedValue)
})

watch(
  () => props.postProperty.formattedValue,
  async (newValue) => {
    textFieldValue.value = newValue
    await processTextFieldValue(props.postProperty.formattedValue)
  },
)
</script>

<template>
  <component
    :is="as"
    ref="textFieldValueRef"
    :class="[
      'surface-post-property-text-field-value',
      propertyValueClass,
      darkMode && 'text-light-text-100',
      !darkMode && 'text-dark-text-100',
    ]"
    v-html="textFieldValue"
  />
</template>
<style lang="postcss">
.surface-post-property-text-field-value a {
  @apply underline;
  @apply text-current;
  text-underline-position: from-font;
  text-decoration-thickness: from-font;
}
</style>
